import {
  FUSION_COUNT,
  fusionCost,
  fusionResult,
  type ItemTemplate,
  type Rarity,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import {
  firstShortfall,
  formatGold,
  goldNeed,
  itemRow,
  type Need,
  needIcon,
  needList,
  section,
  workAction,
} from './bench.js';
import { describeOnHover } from './item-hover.js';
import { STAT_LABELS } from './item-tooltip.js';
import { uiFormat, uiText } from './text.js';

/**
 * The right side of the smelter's window (ADR 0018): the five pieces and the
 * one they make, each as a card with its stats, then what the fusion takes
 * against what the bag and the purse hold, and the button. Only the intent
 * leaves; the bag afterwards is the server's.
 */

export const RARITY_WORDS: Record<Rarity, string> = {
  common: 'commun',
  rare: 'rare',
  epic: 'épique',
  legendary: 'légendaire',
};

/** Non-zero stats of a template, labelled like the tooltip, whatever stats the game has. */
function statLine(template: ItemTemplate): string {
  const labels = STAT_LABELS as Readonly<Record<string, string>>;
  return Object.entries(template.bonus)
    .filter(([, value]) => value !== 0)
    .map(([stat, value]) =>
      uiFormat('{stat} {value}', { stat: uiText(labels[stat] ?? stat), value }),
    )
    .join(' · ');
}

export interface FusionDetailInput {
  template: ItemTemplate;
  count: number;
  /** Pieces of this kind worn: they do not count (ADR 0018). */
  worn: number;
  icons: CssAtlas | null;
  purse: number;
  inReach: boolean;
  pending: boolean;
  onFuse: (templateId: string) => void;
}

export function renderFusionDetail(into: HTMLElement, input: FusionDetailInput): void {
  const { template } = input;
  const made = fusionResult(template);
  const cost = fusionCost(template);
  if (made === undefined || cost === undefined) return;

  const from = itemRow(
    template,
    input.icons,
    uiFormat('{count} × {name}', { count: FUSION_COUNT, name: uiText(template.name) }),
  );
  from.row.classList.add('fusion__from');
  from.meta.textContent = statLine(template);
  describeOnHover(from.slot, template);

  const arrow = document.createElement('p');
  arrow.className = 'fusion__arrow';
  arrow.textContent = '↓';

  const to = itemRow(made, input.icons);
  to.row.classList.add('fusion__to');
  to.meta.textContent = uiText(RARITY_WORDS[made.rarity]);
  const after = document.createElement('span');
  after.className = 'smith__gain fusion__stats';
  after.textContent = statLine(made);
  to.text.append(after);
  describeOnHover(to.slot, made);
  into.append(from.row, arrow, to.row);

  const needs: Need[] = [
    {
      icon: needIcon(template, template.id, input.icons),
      name: uiText(template.name),
      have: input.count,
      need: FUSION_COUNT,
    },
    goldNeed(cost, input.purse),
  ];
  into.append(section(uiText('Coût')), needList(needs));

  if (input.worn > 0) {
    const note = document.createElement('p');
    note.className = 'smith__muted fusion__worn';
    note.textContent = uiFormat(
      input.worn > 1
        ? 'Tu en portes {worn} : retire-les pour qu’elles comptent.'
        : 'Tu en portes {worn} : retire-la pour qu’elle compte.',
      { worn: input.worn },
    );
    into.append(note);
  }

  const why = input.pending
    ? ''
    : (firstShortfall(needs) ?? (input.inReach ? undefined : uiText('Approche-toi du fondeur.')));
  into.append(
    workAction(uiFormat('Fusionner · {gold}', { gold: formatGold(cost) }), why, (button) => {
      button.disabled = true;
      input.onFuse(template.id);
    }),
  );
}
